import { useState, useRef, useCallback } from 'react';
import { TrustCheckResult, TrustCheckResponse, TrustValidation } from '@df-gate-share/shared';
import { apiClient } from '../lib/api';

interface UseTrustCheckReturn {
  trustResults: Map<string, TrustCheckResult>;
  loadingEmails: Set<string>;
  checkEmails: (emails: string[]) => void;
  removeEmail: (email: string) => void;
}

export function useTrustCheck(): UseTrustCheckReturn {
  const [trustResults, setTrustResults] = useState<Map<string, TrustCheckResult>>(new Map());
  const [loadingEmails, setLoadingEmails] = useState<Set<string>>(new Set());

  const pendingEmailsRef = useRef<Set<string>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushPendingEmails = useCallback(async () => {
    const emailsToCheck = Array.from(pendingEmailsRef.current);
    pendingEmailsRef.current = new Set();

    if (emailsToCheck.length === 0) return;

    try {
      const response = await apiClient.post<TrustCheckResponse>('/api/v1/contacts/check-trust', {
        emails: emailsToCheck,
      });

      setTrustResults((prev) => {
        const next = new Map(prev);
        for (const result of response.data.results) {
          next.set(result.email, result);
        }
        return next;
      });
    } catch {
      // Fallback to level 4 (new domain) on API error
      setTrustResults((prev) => {
        const next = new Map(prev);
        for (const email of emailsToCheck) {
          if (!next.has(email)) {
            next.set(email, { email, trustLevel: 4 });
          }
        }
        return next;
      });
    }

    setLoadingEmails((prev) => {
      const next = new Set(prev);
      for (const email of emailsToCheck) {
        next.delete(email);
      }
      return next;
    });
  }, []);

  const checkEmails = useCallback((emails: string[]) => {
    // Filter out already cached emails
    const uncachedEmails = emails.filter(
      (email) => !trustResults.has(email) && !pendingEmailsRef.current.has(email)
    );

    if (uncachedEmails.length === 0) return;

    // Add to pending set
    for (const email of uncachedEmails) {
      pendingEmailsRef.current.add(email);
    }

    // Mark as loading
    setLoadingEmails((prev) => {
      const next = new Set(prev);
      for (const email of uncachedEmails) {
        next.add(email);
      }
      return next;
    });

    // Reset debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      flushPendingEmails();
    }, TrustValidation.DEBOUNCE_MS);
  }, [trustResults, flushPendingEmails]);

  const removeEmail = useCallback((email: string) => {
    setTrustResults((prev) => {
      const next = new Map(prev);
      next.delete(email);
      return next;
    });
    setLoadingEmails((prev) => {
      const next = new Set(prev);
      next.delete(email);
      return next;
    });
    pendingEmailsRef.current.delete(email);
  }, []);

  return { trustResults, loadingEmails, checkEmails, removeEmail };
}
